import React from 'react';
import { useQuery } from '@apollo/client/react';
import type { DocumentNode } from 'graphql';
import type { OperationVariables } from '@apollo/client';

interface QueryProps<TData = unknown, TVariables extends OperationVariables = OperationVariables> {
    query: DocumentNode;
    variables?: TVariables;
    fetchPolicy?: 'cache-first' | 'cache-and-network' | 'network-only' | 'no-cache' | 'cache-only';
    errorPolicy?: 'none' | 'ignore' | 'all';
    pollInterval?: number;
    notifyOnNetworkStatusChange?: boolean;
    skip?: boolean;
    context?: Record<string, unknown>;
    /** Receives the query result. Type is useQuery.Result<TData> (variables default to OperationVariables). */
    children: (result: useQuery.Result<TData, OperationVariables>) => React.ReactNode;
}

/**
 * Query component for Apollo Client 4+.
 * Wraps useQuery to provide the render-prop API previously available from @apollo/client Query.
 */
function Query<TData = unknown, TVariables extends OperationVariables = OperationVariables>({
    query,
    variables,
    fetchPolicy,
    errorPolicy,
    pollInterval,
    notifyOnNetworkStatusChange,
    skip,
    context,
    children,
}: QueryProps<TData, TVariables>): React.ReactElement {
    const result = useQuery<TData, TVariables>(query, {
        ...(variables !== undefined && { variables }),
        ...(fetchPolicy !== undefined && { fetchPolicy }),
        ...(errorPolicy !== undefined && { errorPolicy }),
        ...(pollInterval !== undefined && { pollInterval }),
        ...(notifyOnNetworkStatusChange !== undefined && { notifyOnNetworkStatusChange }),
        ...(skip !== undefined && { skip }),
        ...(context !== undefined && { context }),
    } as useQuery.Options<TData, TVariables>);
    return <>{children(result as useQuery.Result<TData, OperationVariables>)}</>;
}

export { Query };
export type { QueryProps };
